import React, { useContext, useState, useCallback } from 'react'
import { BoxProps, Link, Image } from 'rebass'

import { SStyled } from 'styles/theme'
import SFlex from 'components/opinionless/SFlex'
import SLink from 'components/opinionless/SLink'
import SIcon from './opinionless/SIcon'
import { SLinkProps } from './opinionless/SLink'
import SText from './opinionless/SText'
import { useTranslation } from '../i18n'

import HomeSvg from '@svgs/home.svg'
import GridSvg from '@svgs/grid.svg'
import SettingsSvg from '@svgs/settings.svg'
import HelpSvg from '@svgs/help.svg'
import LogoutSvg from '@svgs/logout.svg'
import SBox from './opinionless/SBox'
import WebsiteSvgDark from '@svgs/web-dark.svg'
import InstagramSvgDark from '@svgs/ig-dark.svg'
import TwitterSvgDark from '@svgs/twitter-dark.svg'
import { useRouter } from 'next/router'

import { useAuth } from 'v2/hooks/useAuth'
import { UserContext } from 'v2/context/user'
import { useSTheme } from 'styles/theme'
import ConditionalRender from 'utils/conditionalRender'
import SButton from './opinionless/SButton'
import { Icon } from '@material-ui/core'
import SConfirmModal from './opinionless/SConfirmModal'

interface Props extends BoxProps {
    empty?: null
}

const SideBar: React.FC<Props> = (props: Props) => {
    const { t } = useTranslation(window.LANG_NS)
    const theme = useSTheme()

    const StyledF4GILogo = SStyled(Image)`
    width: 100%;
    height: auto;
    z-index: 1;
    object-fit: contain;
`
    const { authenticatedUser } = useContext(UserContext)
    const { logoutUser } = useAuth()

    const onClickSignOut = useCallback(
        (event) => {
            event.preventDefault()
            void logoutUser()
        },
        [logoutUser]
    )

    const SocialIcons = SStyled(SFlex)`
    margin-bottom: ${(p) => p.theme.space[4]}px;
`

    const isAdmin = window.location.href.includes('admin')

    return (
        <SFlex
            sx={{
                width: '100%',
                height: '100%',
                bg: 'white',
                flexDirection: 'column',
                overflow: 'scroll',
                alignItems: 'stretch',
                justifyContent: 'space-between',
            }}
        >
            {!isAdmin || process.env.NEXT_PUBLIC_ADMIN_ENABLED !== 'yes' ? (
                <>
                    <SFlex flexDirection="column" px={9} py={3} mt={10}>
                        <SideBarButton
                            title={t('side_bar_button_home')}
                            icon={HomeSvg}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/`}
                        />
                        <SideBarButton
                            title={t('side_bar_button_resources')}
                            icon={GridSvg}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/resources`}
                        />

                        <SideBarButton
                            title={t('side_bar_button_account')}
                            icon={SettingsSvg}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/account`}
                            id="sign-out-button"
                        />
                        <SBox
                            sx={{
                                height: 0.5,
                                bg: 'gray100',
                                my: 5,
                            }}
                        />
                        <SideBarButton
                            title={t('side_bar_button_help')}
                            icon={HelpSvg}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/help`}
                        />

                        <SideBarButton
                            title={t('side_bar_button_sign_out')}
                            icon={LogoutSvg}
                            href="#"
                            onClick={onClickSignOut}
                            id="sign-out-button"
                        />
                    </SFlex>
                    <SFlex
                        flexDirection="column"
                        p={8}
                        sx={{
                            color: theme.colors.tertiaryColor,
                        }}
                    >
                        <SText fontSize={3} fontWeight="medium" mb={4}>
                            {t('side_bar_bottom_header')}
                        </SText>
                        <SText
                            fontSize={0}
                            mb={4}
                            lineHeight={1.3}
                            sx={{
                                whiteSpace: 'pre-line',
                            }}
                        >
                            {t('side_bar_bottom_copyright')}
                        </SText>

                        <SText fontSize={0} lineHeight={1.3}>
                            {t('side_bar_bottom_f4gi_description')}
                        </SText>
                    </SFlex>
                </>
            ) : (
                <>
                    <SFlex flexDirection="column" px={9} py={3} mt={10}>
                        <SideBarButton
                            googleIcon={'homeq'}
                            title={'Home'}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/admin/`}
                        />
                        <ConditionalRender if={authenticatedUser?.isSuperuser === 2}>
                            <SideBarButton
                                googleIcon={'paid'}
                                title={'Payments'}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/admin/payments/`}
                            />
                        </ConditionalRender>
                        <ConditionalRender if={authenticatedUser?.isSuperuser === 2}>
                            <SideBarButton
                                googleIcon={'sms'}
                                title={'Text Messages'}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/admin/text-messages`}
                            />
                        </ConditionalRender>
                        <ConditionalRender if={authenticatedUser?.isSuperuser === 2}>
                            <SideBarButton
                                googleIcon={'supervisor_account'}
                                title={'Users'}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/admin/users`}
                                id="sign-out-button"
                            />
                        </ConditionalRender>
                        <ConditionalRender if={authenticatedUser?.isSuperuser === 2}>
                            <SideBarButton
                                googleIcon={'analytics'}
                                title={'Stats'}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/admin/stats`}
                            />
                        </ConditionalRender>
                        <ConditionalRender if={authenticatedUser?.isSuperuser === 2}>
                            <SideBarButton
                                googleIcon={'settings'}
                                title={'Configurations'}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/admin/configurations`}
                            />
                        </ConditionalRender>

                        <SBox
                            sx={{
                                height: 0.5,
                                bg: 'gray100',
                                my: 5,
                            }}
                        />

                        <SideBarButton
                            googleIcon={'assignment'}
                            title={'Applications'}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/admin/applications`}
                        />
                        <SideBarButton
                            googleIcon={'map'}
                            title={'Heat Map Signup'}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/admin/heat-map-signup/`}
                        />

                        <ConditionalRender if={window.ORGANIZATION_CONFIGS.isTypeformBound}>
                            <SideBarButton
                                googleIcon={'inventory'}
                                title={'Survey Summary'}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/admin/survey`}
                            />
                        </ConditionalRender>

                        <SideBarButton
                            title={t('side_bar_button_sign_out')}
                            icon={LogoutSvg}
                            href="javascript:void(0)"
                            onClick={onClickSignOut}
                            id="sign-out-button"
                        />
                    </SFlex>
                    <SFlex
                        flexDirection="column"
                        p={8}
                        sx={{
                            color: theme.colors.tertiaryColor,
                        }}
                    >
                        <SText fontSize={3} fontWeight="medium" mb={4}>
                            {t('side_bar_bottom_header')}
                        </SText>
                        <ConditionalRender if={window.ORGANIZATION.version === 2}>
                            <SocialIcons>
                                <Link target="__blank" href={'/'} height={20}>
                                    <SIcon icon={WebsiteSvgDark} size={18} mr={3} tintColor="grey" />
                                </Link>

                                <Link target="__blank" href={'/'} height={20}>
                                    <SIcon icon={TwitterSvgDark} size={18} mr={3} tintColor="grey" />
                                </Link>

                                <Link target="__blank" href={'/'} height={20}>
                                    <SIcon icon={InstagramSvgDark} size={18} mr={3} tintColor="grey" />
                                </Link>
                            </SocialIcons>
                            <StyledF4GILogo
                                width="100%"
                                height="auto"
                                pb={5}
                                src={'/F4GI_Full Logo_Black.png'}
                                alt={'logo'}
                            />
                        </ConditionalRender>
                        <SText
                            fontSize={0}
                            mb={4}
                            lineHeight={1.3}
                            sx={{
                                whiteSpace: 'pre-line',
                            }}
                        >
                            {t('side_bar_bottom_copyright')}
                        </SText>

                        <SText fontSize={0} lineHeight={1.3}>
                            {t('side_bar_bottom_f4gi_description')}
                        </SText>
                    </SFlex>
                </>
            )}
        </SFlex>
    )
}

interface SideBarButtonProps extends SLinkProps {
    title: string
    icon?: any
    googleIcon?: string
    tintColor?: string
    textColor?: string
    fontWeight?: string
    iconSize?: number
}

export const SideBarButton: React.FC<SideBarButtonProps> = (props: SideBarButtonProps) => {
    const { icon, title, href, googleIcon, tintColor, iconSize, textColor, fontWeight, ...otherProps } = props

    const router = useRouter()
    const theme = useSTheme()

    const selected = router.pathname === href

    return (
        <SLink
            href={href}
            sx={{
                p: 5,
                bg: selected ? theme.colors.secondaryColor : 'white',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'row',
                minWidth: 'fit-content',
                alignItems: 'center',
                my: 4,
                ...(!selected
                    ? {
                          '&:hover': {
                              bg: theme.colors.backgroundColor,
                          },
                      }
                    : {}),
            }}
            {...otherProps}
        >
            <ConditionalRender if={icon}>
                <SIcon
                    icon={icon}
                    sx={{ mr: 5 }}
                    size={iconSize || 20}
                    tintColor={selected ? 'white' : tintColor || theme.colors.textColor || theme.colors.secondaryColor}
                />
            </ConditionalRender>
            <ConditionalRender if={googleIcon}>
                <Icon
                    style={{
                        color: selected ? 'white' : theme.colors.textColor || theme.colors.secondaryColor,
                        marginRight: '5px',
                    }}
                >
                    {googleIcon}
                </Icon>
            </ConditionalRender>
            <SText
                color={selected ? 'white' : textColor || theme.colors.tertiaryColor}
                fontSize={4}
                fontWeight={fontWeight ? fontWeight : 'bold'}
            >
                {title}
            </SText>
        </SLink>
    )
}

export default SideBar
